import _defaultTo from 'lodash/defaultTo';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
// Serialization
import { deserialize, serialize } from 'serializr';
import {
  userAlertingSchema, userCallbackSchema, userSchema, meSchema,
} from './schemas';
// Permissions
import permissions from '../constants/Permissions';
// Custom
import { SEARCHFIELD_TYPES } from '../constants/App';
import { joinNonEmpty, formatPhoneNumberSubmit, findInList } from '../utils/Tools';
// Colors
import { userRollbackStrategyPalette } from '../components/core/colors';

/**
 * Serialization
 */
const serializerKey = 'user';

const serializeUserCallback = ({ version, uri, subscriptions }) => ({
  ...serialize(userCallbackSchema, {
    ...(version && { version }),
    ...(uri && { uri }),
    ...(subscriptions && { subscriptions }),
  }),
});

const serializeUserAlerting = ({ alertingId, media, config }) => ({
  ...serialize(userAlertingSchema, {
    alerting_id: _defaultTo(alertingId, null),
    media: _defaultTo(media, null),
    ...(config && { config }),
  }),
});

const deserializeUser = (data) => deserialize(userSchema, data);

const serializeUser = ({
  locale,
  firstname,
  lastname,
  email,
  password,
  phone,
  comment,
  userPermissions,
  callback,
  userAlertings,
  rollbackStrategy,
  companyId,
}) => ({
  [serializerKey]: {
    ...serialize(userSchema, {
      locale: _defaultTo(locale, null),
      firstname: _defaultTo(firstname, null),
      lastname: _defaultTo(lastname, null),
      ...(email && { email }),
      ...(password && { password }),
      ...(phone && { phone: formatPhoneNumberSubmit(phone) }),
      ...(comment && { comment }),
      company_id: _defaultTo(companyId, null),
      ...(userPermissions && { permissions: userPermissions }),
      ...(callback && { callback: serializeUserCallback(callback) }),
      ...(userAlertings && { user_alertings: userAlertings }),
      rollback_strategy: _defaultTo(rollbackStrategy, null),
    }),
  },
});

const serializeMe = ({
  locale,
  firstname,
  lastname,
  email,
  currentPassword,
  password,
  passwordConfirmation,
  phone,
  callback,
  userAlertings,
  companyName,
  invoiceAddress,
  nationalIdentifier,
}) => ({
  [serializerKey]: {
    ...serialize(meSchema, {
      locale: _defaultTo(locale, null),
      ...(firstname && { firstname }),
      ...(lastname && { lastname }),
      ...(email && { email }),
      ...(currentPassword && { currentPassword }),
      ...(password && { password }),
      ...(passwordConfirmation && { passwordConfirmation }),
      ...(phone && { phone: formatPhoneNumberSubmit(phone) }),
      ...(callback && { callback: serializeUserCallback(callback) }),
      ...(userAlertings && { user_alertings: userAlertings }),
      ...(companyName && { companyName }),
      ...(invoiceAddress && { invoiceAddress }),
      ...(nationalIdentifier && { nationalIdentifier }),
    }),
  },
});

/**
 * Lists
 */
const userCallbackSubscriptionsList = [
  'async_operation',
  'device_log',
  'installation_log',
];

const userRollbackStrategyList = [
  {
    key: 'always_commit',
    value: 'always_commit',
    labelId: 'user.rollbackStrategy.alwaysCommit',
    icon: 'userRollbackStrategyAlwaysCommit',
    color: userRollbackStrategyPalette.alwaysCommit,
  },
  {
    key: 'rollback',
    value: 'rollback',
    labelId: 'user.rollbackStrategy.rollback',
    icon: 'userRollbackStrategyRollback',
    color: userRollbackStrategyPalette.rollback,
  },
];

/**
 * Getters
 */
const userGetRollbackStrategyAlwaysCommit = userRollbackStrategyList[0];

/**
 * Utils
 */
const userFullName = (user) => (user ? joinNonEmpty([user.firstname, user.lastname], ' ') : '');

const userTitle = (userData) => userFullName(userData);

const userRollbackStrategy = (user) => findInList(userRollbackStrategyList, 'value', user, 'rollback_strategy');

/**
 * Model
 */
const user = {
  // Name and icon
  name: 'user',
  icon: 'user',

  // Serialization
  deserialize: deserializeUser,
  serialize: serializeUser,
  serializeAlerting: serializeUserAlerting,
  serializeMe,

  // Lists
  callbackSubscriptionsList: userCallbackSubscriptionsList,
  rollbackStrategyList: userRollbackStrategyList,

  // Getters
  getRollbackStrategyAlwaysCommit: userGetRollbackStrategyAlwaysCommit,

  // Utils
  title: userTitle,
  fullName: userFullName,
  rollbackStrategy: userRollbackStrategy,

  // List features
  listFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [
        { field: 'lastname', dir: 'asc' },
        { field: 'firstname', dir: 'asc' },
      ],
    },

    // Columns
    columnsQuantity: (hasPermission) => ({
      xs: 1,
      sm: 1,
      md: 2,
      lg: 5 + (hasPermission({ needed: permissions.pAdmin }) | 0),
      xl: 5 + (hasPermission({ needed: permissions.pAdmin }) | 0),
    }),
    columns: [
      {
        key: 'lastname',
        labelId: 'field.lastname',
        field: 'lastname',
        sortable: true,
      },
      {
        key: 'firstname',
        labelId: 'field.firstname',
        field: 'firstname',
        sortable: true,
        secondarySorts: [{ field: 'lastname', dir: 'asc' }],
        className: 'hidden-sm-down',
      },
      {
        key: 'email',
        labelId: 'field.email',
        field: 'email',
        sortable: true,
        secondarySorts: [
          { field: 'lastname', dir: 'asc' },
          { field: 'firstname', dir: 'asc' },
        ],
        className: 'hidden-md-down',
      },
      {
        key: 'permissions',
        labelId: 'field.permissions',
        field: 'permissions',
        sortable: false,
        className: 'hidden-md-down table-content-center',
      },
      {
        key: 'rollbackStrategy',
        permissions: {
          needed: permissions.pAdmin,
        },
        labelId: 'field.rollbackStrategy',
        field: 'rollbackStrategy',
        sortable: false,
        className: 'hidden-md-down table-content-1 table-content-center',
      },
      {
        key: 'callback',
        labelId: 'field.callback',
        field: 'callback',
        sortable: false,
        className: 'hidden-md-down table-content-1 table-content-center',
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'lastname',
        labelId: 'field.lastname',
        field: 'lastname',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'lastname',
        },
      },
      {
        key: 'firstname',
        labelId: 'field.firstname',
        field: 'firstname',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'firstname',
        },
      },
      {
        key: 'product',
        permissions: {
          needed: permissions.pAdmin,
        },
        labelId: 'field.product',
        field: 'product',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'product',
        },
      },
      {
        key: 'email',
        labelId: 'field.email',
        field: 'email',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'email',
        },
      },
    ],
  },
};

module.exports = user;



// WEBPACK FOOTER //
// ./models/user.js